import React from 'react';
import {
  HStack,
  Badge,
  BadgeText,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipText,
  Box,
  Text,
  VStack,
  Divider,
} from '@gluestack-ui/themed';
import { Check, X } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useTenantGradingSettings } from '../../../../../common/context/TenantSettingsStore';
import type { DimensionCriterion } from './types';

interface JustificationBadge {
  criteria: string;
  points: string;
  reason: string;
}

interface JustificationBadgesProps {
  justification?: string;
  dimension?: string;
  dimensionCriteria?: DimensionCriterion[];
}

/**
 * Component that visualizes evaluation justifications as a list of inline badges
 * Parses justification string in format: crit:deductedpoints:reason
 * Multiple justifications are separated by semicolons
 *
 * Example: "Study Findings:-2:missing X;Method:-1:incomplete Y"
 */
const JustificationBadges = ({ justification, dimension, dimensionCriteria = [] }: JustificationBadgesProps) => {
  const { t } = useTranslation();
  const { dimensionColors, dimensionLightColors } = useTenantGradingSettings();

  if (!justification) return null;

  // Parse the semicolon-separated justifications
  const badges: JustificationBadge[] = justification
    .split(';')
    .filter((item) => item.trim())
    .map((item) => {
      const [criteria, points, ...reasonParts] = item.split(':');
      const reason = reasonParts.join(':'); // Rejoin any reason parts that might contain colons

      return {
        criteria: criteria?.trim() || '',
        points: points?.trim() || '0',
        reason: reason?.trim() || '',
      };
    });

  if (badges.length === 0) return null;

  // Get dimension colors
  const dimensionColor = dimension && dimensionColors[dimension];
  const dimensionBgColor = dimension && dimensionLightColors[dimension];

  // Helper function to get expected criteria from rubric
  const getExpectedCriteria = (criteriaName: string) => {
    if (!dimensionCriteria || dimensionCriteria.length === 0) return null;

    // Find the criterion that matches the criteria name
    const criterion = dimensionCriteria.find(
      (crit) => crit.name.toLowerCase().includes(criteriaName.toLowerCase())
        || criteriaName.toLowerCase().includes(crit.name.toLowerCase()),
    );

    return criterion?.descriptor || null;
  };

  // Render appropriate indicator based on points value
  const renderPointsIndicator = (points: string) => {
    // For 'ok' (full points), show check mark
    if (points.toLowerCase() === 'ok') {
      return (
        <Badge
          size="md"
          bg={dimensionColor}
          pl="$2"
          pr="$3"
          h="$6"
          borderRadius="$full"
          borderBottomLeftRadius={0}
          borderTopLeftRadius={0}
          alignItems="center"
          justifyContent="center"
        >
          <BadgeText color="white">
            <Icon as={Check} size="xs" ml="$0.5" mt="$1" color="white" />
          </BadgeText>
        </Badge>
      );
    }

    // For fail (x) or numerical deductions, show point badge
    return (
      <Badge
        size="md"
        bg={dimensionColor}
        pl="$2"
        pr="$3"
        h="$6"
        borderRadius="$full"
        borderBottomLeftRadius={0}
        borderTopLeftRadius={0}
        alignItems="center"
        justifyContent="center"
      >
        <BadgeText color="white" size="xs">
          {points.toLowerCase() === 'x' ? <Icon as={X} size="xs" ml="$0.5" mt="$1" color="white" /> : points}
        </BadgeText>
      </Badge>
    );
  };

  // Render a badge with tooltip for the reason if available
  const renderBadgeWithTooltip = (badge: JustificationBadge, index: number) => {
    const badgeElement = (
      <HStack key={`justification-dimension-${dimension}-${index}`} mr="$2" mb="$1">
        <Badge
          size="sm"
          variant="solid"
          bg={dimensionBgColor}
          opacity={0.85}
          borderRadius="$full"
          borderBottomRightRadius={0}
          borderTopRightRadius={0}
          pr={0}
          h="$6"
        >
          <BadgeText size="xs" mx="$1.5" pr="$1" color={dimensionColor}>
            {badge.criteria}
          </BadgeText>
        </Badge>
        {renderPointsIndicator(badge.points)}
      </HStack>
    );

    // Get expected criteria from rubric
    const expectedCriteria = getExpectedCriteria(badge.criteria);

    // If there's no reason and no expected criteria, just return the badge
    if (!badge.reason && !expectedCriteria) {
      return badgeElement;
    }

    // Build tooltip content
    const tooltipContent = (
      <VStack space="xs">
        {expectedCriteria && (
          <VStack space="xs">
            <Text color="white" size="xs" fontWeight="bold">
              {t('lecturer.evalDesk.justificationTooltip.expected')}
              :
            </Text>
            <Text color="white" size="xs">
              {expectedCriteria}
            </Text>
          </VStack>
        )}
        {badge.reason && expectedCriteria && <Divider />}
        {badge.reason && (
          <VStack space="xs">
            <Text color="white" size="xs" fontWeight="bold">
              {t('lecturer.evalDesk.justificationTooltip.issue')}
              :
            </Text>
            <Text color="white" size="xs">
              {badge.reason}
            </Text>
          </VStack>
        )}
      </VStack>
    );

    // Otherwise, wrap it in a tooltip
    return (
      <Tooltip
        key={`tooltip-${index}`}
        placement="top"
        openDelay={200}
        trigger={(triggerProps) => <Box {...triggerProps}>{badgeElement}</Box>}
      >
        <TooltipContent bg="$backgroundLight800" maxWidth={350}>
          <TooltipText>{tooltipContent}</TooltipText>
        </TooltipContent>
      </Tooltip>
    );
  };

  return <HStack flexWrap="wrap">{badges.map((badge, index) => renderBadgeWithTooltip(badge, index))}</HStack>;
};

export default JustificationBadges;
